/**
 * Parse and validate a proxy routing config chosen for Proxy Resources' Import.
 *
 * POST /resources/proxy/import takes { config: { upstreams: {…}, routes: […] } }.
 * Import used to post an empty { upstreams: {}, routes: [] } with no file at all.
 * This accepts a JSON file of that shape (Export downloads YAML, which the
 * browser has no parser for here, so it is rejected with a clear message), and
 * refuses a config with nothing in it.
 */
export interface ProxyImportConfig {
  upstreams: Record<string, unknown>
  routes: unknown[]
}

export type ProxyImportResult =
  | { ok: true; config: ProxyImportConfig; upstreamCount: number; routeCount: number }
  | { ok: false; error: string }

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)

export function parseProxyImport(text: string, fileName = ''): ProxyImportResult {
  if (/\.ya?ml$/i.test(fileName)) {
    return { ok: false, error: 'YAML files cannot be imported here. Choose a JSON file with "upstreams" and "routes".' }
  }
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    return { ok: false, error: 'The file is not valid JSON.' }
  }
  // Accept the request body shape too: { config: { upstreams, routes } }.
  if (isPlainObject(raw) && isPlainObject(raw.config) && !('routes' in raw)) raw = raw.config
  if (!isPlainObject(raw)) {
    return { ok: false, error: 'Expected a JSON object with "upstreams" and "routes".' }
  }
  const upstreams = raw.upstreams ?? {}
  const routes = raw.routes ?? []
  if (!isPlainObject(upstreams)) return { ok: false, error: '"upstreams" must be an object keyed by upstream name.' }
  if (!Array.isArray(routes)) return { ok: false, error: '"routes" must be an array.' }
  const upstreamCount = Object.keys(upstreams).length
  const routeCount = routes.length
  if (upstreamCount === 0 && routeCount === 0) {
    return { ok: false, error: 'The file has no upstreams and no routes; there is nothing to import.' }
  }
  return { ok: true, config: { ...raw, upstreams, routes }, upstreamCount, routeCount }
}
